import { useCallback, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useUserResetPasswordMutation } from "@/v2-backoffice-client/user/resetPassword/user.resetPassword.mutation"
import UserResetPasswordForm from "@/v2-backoffice-client/user/resetPassword/UserResetPasswordForm"

export type UserResetPasswordDialogProps = DialogProps & {
  userId: number
}

function UserResetPasswordDialog(props: UserResetPasswordDialogProps) {
  const { userId, onOpenChange, ...dialogProps } = props
  const [isPasswordValid, setIsPasswordValid] = useState(false)

  const [resetPassword, { loading, error }] = useUserResetPasswordMutation()
  const handleValidityChange = useCallback((isValid: boolean) => {
    setIsPasswordValid(isValid)
  }, [])

  return (
    <Dialog onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Reset Password</Dialog.Title>
          <Dialog.Description>
            Set a new password for this user. It is emailed to them together
            with a sign-in link.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <UserResetPasswordForm
          onValidityChange={handleValidityChange}
          onSubmit={(value, e) => {
            e.preventDefault()
            resetPassword({
              variables: {
                id: userId,
                password: value.password,
                forceChangePasswordNextSignIn:
                  value.forceChangePasswordNextSignIn,
              },
              // onCompleted only fires without GraphQL errors (Apollo keeps
              // errors on `error` for the ErrorMessage above), so this is
              // the success path: say so and close — the operator was left
              // guessing when the dialog stayed open after a 200.
              onCompleted() {
                toast({
                  variant: "success",
                  title: "Password reset",
                  description: value.forceChangePasswordNextSignIn
                    ? "The user signs in with the temporary password once and is asked to choose a new one."
                    : "The new password is in effect and stays until the user changes it.",
                })
                onOpenChange?.(false)
              },
            })
          }}
        />

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
            disabled={!isPasswordValid}
            form="reset-password-form"
          >
            Reset Password
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UserResetPasswordDialog
